/* ============================================
   TABLE OF CONTENTS COMPONENT
   The Creative Orchestra - GEMINI Framework
   Novel table of contents with parts and chapters
   ============================================ */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  BookOpen, 
  ChevronDown, 
  ChevronRight, 
  Check,
  Bookmark,
  Clock,
} from 'lucide-react';
import { chapters, novelParts, chapterList } from '@data';
import useBookmark from '@hooks/useBookmark';

/**
 * Table of Contents Component
 * Full table of contents with parts and chapters
 * 
 * @param {Object} props
 * @param {string} props.currentChapterId - Currently reading chapter
 * @param {boolean} props.isOpen - Whether TOC is visible (for modal mode)
 * @param {Function} props.onClose - Callback to close TOC
 * @param {string} props.variant - 'modal' | 'sidebar' | 'inline'
 */
const TableOfContents = ({
  currentChapterId,
  isOpen = true,
  onClose,
  variant = 'inline',
  className = '',
}) => {
  const [expandedParts, setExpandedParts] = useState(() => {
    // Expand the part containing current chapter by default
    const currentChapter = chapterList.find(ch => ch.id === currentChapterId);
    return currentChapter ? [currentChapter.part] : [1];
  });

  const { isChapterVisited, bookmark } = useBookmark();

  // Toggle part expansion
  const togglePart = (partNumber) => {
    setExpandedParts(prev => 
      prev.includes(partNumber)
        ? prev.filter(p => p !== partNumber)
        : [...prev, partNumber]
    );
  };

  // Render based on variant
  if (variant === 'modal') {
    return (
      <AnimatePresence>
        {isOpen && (
          <TocModal 
            onClose={onClose}
            currentChapterId={currentChapterId}
            expandedParts={expandedParts}
            togglePart={togglePart}
            isChapterVisited={isChapterVisited}
            bookmark={bookmark}
          />
        )}
      </AnimatePresence>
    );
  }

  if (variant === 'sidebar') {
    return (
      <TocSidebar
        currentChapterId={currentChapterId}
        expandedParts={expandedParts}
        togglePart={togglePart}
        isChapterVisited={isChapterVisited}
        bookmark={bookmark}
        className={className}
      />
    );
  }

  // Inline variant
  return (
    <TocInline
      currentChapterId={currentChapterId}
      expandedParts={expandedParts}
      togglePart={togglePart}
      isChapterVisited={isChapterVisited}
      bookmark={bookmark}
      className={className}
    />
  );
};

/**
 * TOC Modal Component
 */
const TocModal = ({
  onClose,
  currentChapterId,
  expandedParts,
  togglePart,
  isChapterVisited,
  bookmark,
}) => {
  const navigate = useNavigate();

  const handleChapterClick = (chapterId) => {
    navigate(`/novel/${chapterId}`);
    onClose?.();
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Modal content */}
      <motion.div
        className="relative w-full max-w-2xl max-h-[80vh] 
          bg-[#0f1419] border border-white/10 rounded-2xl
          overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-indigo-400" />
            <div>
              <h2 className="text-xl font-display font-semibold text-white">
                {chapters.title}
              </h2>
              <p className="text-sm text-gray-500">
                {chapters.totalChapters} chapters • {chapters.estimatedReadingTime}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 
              hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Bookmark resume */}
        {bookmark && (
          <div className="px-6 py-3 bg-indigo-500/10 border-b border-white/10">
            <button
              onClick={() => handleChapterClick(bookmark.chapterId)}
              className="flex items-center gap-2 text-sm text-indigo-400 
                hover:text-indigo-300 transition-colors"
            >
              <Bookmark className="w-4 h-4" />
              Continue reading: {bookmark.chapterTitle}
              <span className="text-gray-500">({bookmark.scrollPercentage}%)</span>
            </button>
          </div>
        )}

        {/* Chapter list */}
        <div className="flex-1 overflow-y-auto p-6">
          <TocContent
            currentChapterId={currentChapterId}
            expandedParts={expandedParts}
            togglePart={togglePart}
            isChapterVisited={isChapterVisited}
            onChapterClick={handleChapterClick}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * TOC Sidebar Component
 */
const TocSidebar = ({
  currentChapterId,
  expandedParts,
  togglePart,
  isChapterVisited,
  bookmark,
  className,
}) => {
  return (
    <aside className={`w-72 flex-shrink-0 ${className}`}>
      <div className="sticky top-24 bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <h3 className="font-semibold text-white">Contents</h3>
        </div>

        {/* Chapter list */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          <TocContent
            currentChapterId={currentChapterId}
            expandedParts={expandedParts}
            togglePart={togglePart}
            isChapterVisited={isChapterVisited}
            compact
          />
        </div>
      </div>
    </aside>
  );
};

/**
 * TOC Inline Component
 */
const TocInline = ({
  currentChapterId,
  expandedParts,
  togglePart,
  isChapterVisited,
  bookmark,
  className,
}) => {
  return (
    <div className={`bg-white/5 border border-white/10 rounded-xl p-6 ${className}`}>
      <h2 className="text-xl font-display font-semibold text-white mb-6">
        Table of Contents
      </h2>
      <TocContent
        currentChapterId={currentChapterId}
        expandedParts={expandedParts}
        togglePart={togglePart}
        isChapterVisited={isChapterVisited}
      />
    </div>
  );
};

/**
 * TOC Content (shared between variants)
 */
const TocContent = ({
  currentChapterId,
  expandedParts,
  togglePart,
  isChapterVisited,
  onChapterClick,
  compact = false,
}) => {
  return (
    <div className="space-y-4">
      {novelParts.map((part) => {
        const isExpanded = expandedParts.includes(part.number);
        const partChapters = part.chapterIds.map(id => 
          chapterList.find(ch => ch.id === id)
        ).filter(Boolean);
        const readCount = partChapters.filter(ch => isChapterVisited(ch.id)).length;

        return (
          <div key={part.number}>
            {/* Part header */}
            <button
              onClick={() => togglePart(part.number)}
              className="w-full flex items-center justify-between py-2 
                text-left hover:text-indigo-400 transition-colors group"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <span className={`${compact ? 'text-sm' : ''} font-medium text-white 
                  group-hover:text-indigo-400`}>
                  {part.title}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                {readCount}/{partChapters.length}
              </span>
            </button>

            {/* Part subtitle */}
            {!compact && part.subtitle && !isExpanded && (
              <p className="text-xs text-gray-500 ml-6 -mt-1 mb-2">
                {part.subtitle}
              </p>
            )}

            {/* Chapters */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="ml-6 space-y-1 py-2">
                    {partChapters.map((chapter) => {
                      const isCurrent = chapter.id === currentChapterId;
                      const isVisited = isChapterVisited(chapter.id);

                      const ChapterWrapper = onChapterClick ? 'button' : Link;
                      const wrapperProps = onChapterClick 
                        ? { onClick: () => onChapterClick(chapter.id) }
                        : { to: `/novel/${chapter.id}` };

                      return (
                        <ChapterWrapper
                          key={chapter.id}
                          {...wrapperProps}
                          className={`
                            w-full flex items-center gap-2 py-1.5 px-2 rounded-lg
                            text-left transition-colors
                            ${compact ? 'text-xs' : 'text-sm'}
                            ${isCurrent 
                              ? 'bg-indigo-500/20 text-indigo-300' 
                              : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }
                          `}
                        >
                          {/* Status indicator */}
                          <span className="w-4 flex-shrink-0">
                            {isCurrent ? (
                              <span className="w-2 h-2 rounded-full bg-indigo-400 block" />
                            ) : isVisited ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : null}
                          </span>

                          {/* Chapter info */}
                          <span className="flex-grow truncate">
                            {chapter.type === 'prologue' && 'Prologue: '}
                            {chapter.type === 'epilogue' && 'Epilogue: '}
                            {chapter.type === 'chapter' && `${chapter.number}. `}
                            {chapter.title}
                          </span>

                          {/* Reading time */}
                          {!compact && chapter.readingTime && (
                            <span className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
                              <Clock className="w-3 h-3" />
                              {chapter.readingTime}
                            </span>
                          )}
                        </ChapterWrapper>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Mini TOC (for mobile/compact displays)
 */
export const MiniToc = ({ currentChapterId, className = '' }) => {
  const currentChapter = chapterList.find(ch => ch.id === currentChapterId);
  const currentIndex = chapterList.findIndex(ch => ch.id === currentChapterId);

  return (
    <div className={`flex items-center gap-4 text-sm ${className}`}>
      <span className="text-gray-500">
        Chapter {currentIndex + 1} of {chapterList.length}
      </span>
      {currentChapter?.partTitle && (
        <span className="text-gray-600 hidden sm:inline">
          {currentChapter.partTitle}
        </span>
      )}
    </div>
  );
};

export default TableOfContents;
